import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import {
  stockPortfolioService,
  StockCompany,
  StockPrice,
  StockTransaction,
  StockDividend,
  PriceUpdateData,
  StockFinancialReport,
  FinancialReportFormData
} from '../../services/stockPortfolioService';
import './StockPortfolio.css';

interface CompanyDetailProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const CompanyDetail: React.FC<CompanyDetailProps> = ({ user, onClose, onToggleSidebar }) => {
  const { stockCode } = useParams<{ stockCode: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<StockCompany | null>(null);
  const [priceHistory, setPriceHistory] = useState<StockPrice[]>([]);
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [dividends, setDividends] = useState<StockDividend[]>([]);
  const [currentQuantity, setCurrentQuantity] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [averageCost, setAverageCost] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [currentValue, setCurrentValue] = useState<number>(0);
  const [gainLoss, setGainLoss] = useState<number>(0);
  const [gainLossPercent, setGainLossPercent] = useState<number>(0);
  const [realizedGain, setRealizedGain] = useState<number>(0);
  const [unrealizedGain, setUnrealizedGain] = useState<number>(0);
  const [totalDividends, setTotalDividends] = useState<number>(0);
  const [totalReturn, setTotalReturn] = useState<number>(0);
  const [totalReturnPercent, setTotalReturnPercent] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'price' | 'transactions' | 'financials'>('overview');
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [expandedPriceDate, setExpandedPriceDate] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState<PriceUpdateData>({
    stock_code: stockCode || '',
    price: 0,
    price_date: stockPortfolioService.getCurrentDate()
  });
  const [showStockDropdown, setShowStockDropdown] = useState(false);
  const [stockSearchQuery, setStockSearchQuery] = useState<string>('');
  const [allCompanies, setAllCompanies] = useState<StockCompany[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<StockCompany[]>([]);
  const [financialReports, setFinancialReports] = useState<StockFinancialReport[]>([]);
  const [showFinancialReportForm, setShowFinancialReportForm] = useState(false);
  const [editingReport, setEditingReport] = useState<StockFinancialReport | null>(null);
  const [expandedReportId, setExpandedReportId] = useState<number | null>(null);
  const [reportFilter, setReportFilter] = useState<'all' | 'quarterly' | 'yearly'>('all');
  const [openFormulaPopup, setOpenFormulaPopup] = useState<string | null>(null);
  const [newFinancialReport, setNewFinancialReport] = useState<FinancialReportFormData>({
    stock_code: stockCode || '',
    report_type: 'quarterly',
    fiscal_year: new Date().getFullYear(),
    quarter: 1,
    period_start_date: '',
    period_end_date: '',
    report_date: stockPortfolioService.getCurrentDate()
  });

  useEffect(() => {
    if (stockCode) {
      loadCompanyData();
      loadFinancialReports();
    }
  }, [stockCode]);

  useEffect(() => {
    if (stockCode) {
      setNewFinancialReport(prev => ({
        ...prev,
        stock_code: stockCode
      }));
    }
  }, [stockCode]);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!user?.id) return;

      try {
        const response = await stockPortfolioService.getCompanies();
        if (response.success && response.companies) {
          setAllCompanies(response.companies);
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
      }
    };

    fetchCompanies();
  }, [user?.id]);

  useEffect(() => {
    if (!stockSearchQuery.trim()) {
      setFilteredCompanies(allCompanies.filter(c => c.stock_code !== stockCode));
      return;
    }

    const query = stockSearchQuery.toLowerCase().trim();
    const filtered = allCompanies.filter(company => {
      const matchesCode = company.stock_code.toLowerCase().includes(query);
      const matchesName = company.company_name.toLowerCase().includes(query);
      return (matchesCode || matchesName) && company.stock_code !== stockCode;
    });

    setFilteredCompanies(filtered);
  }, [stockSearchQuery, allCompanies, stockCode]);

  const loadFinancialReports = async () => {
    if (!stockCode) return;

    try {
      const reportType = reportFilter === 'all' ? undefined : reportFilter;
      const response = await stockPortfolioService.getFinancialReports(stockCode, reportType);
      if (response.success && response.reports) {
        setFinancialReports(response.reports);
      }
    } catch (err) {
      console.error('Error loading financial reports:', err);
    }
  };

  useEffect(() => {
    if (stockCode && activeTab === 'financials') {
      loadFinancialReports();
    }
  }, [stockCode, reportFilter, activeTab]);

  const loadCompanyData = async () => {
    if (!stockCode || !user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [companyResponse, priceResponse, transactionResponse, dividendResponse] = await Promise.all([
        stockPortfolioService.getCompanyByCode(stockCode),
        stockPortfolioService.getStockPriceHistory(stockCode),
        stockPortfolioService.getUserTransactions(user.id.toString(), stockCode),
        stockPortfolioService.getUserDividends(user.id.toString(), stockCode)
      ]);

      if (companyResponse.success && companyResponse.company) {
        setCompany(companyResponse.company);
      }

      if (priceResponse.success && priceResponse.prices) {
        setPriceHistory(priceResponse.prices);

        // Get current price (latest price)
        if (priceResponse.prices.length > 0) {
          const sortedPrices = [...priceResponse.prices].sort((a, b) =>
            new Date(b.price_date).getTime() - new Date(a.price_date).getTime()
          );
          setCurrentPrice(sortedPrices[0].price);
        }
      }

      // Calculate holdings from transactions and stock dividends
      let transactionQuantity = 0;
      let transactionCost = 0;
      let totalBuyQuantity = 0;
      let totalBuyCost = 0;
      let totalSellQuantity = 0;
      let totalSellProceeds = 0;
      let calculatedRealizedGain = 0;

      if (transactionResponse.success && transactionResponse.transactions) {
        const txns = transactionResponse.transactions;
        setTransactions(txns);

        // First, calculate total buys to determine average cost
        txns.forEach(transaction => {
          if (transaction.transaction_type === 'buy') {
            totalBuyQuantity += transaction.quantity;
            totalBuyCost += transaction.quantity * transaction.price_per_share;
          }
        });

        // Calculate average cost per share from all buys
        const avgCostPerShare = totalBuyQuantity > 0 ? totalBuyCost / totalBuyQuantity : 0;

        // Calculate quantity and cost from transactions
        txns.forEach(transaction => {
          if (transaction.transaction_type === 'buy') {
            transactionQuantity += transaction.quantity;
            transactionCost += transaction.quantity * transaction.price_per_share;
          } else if (transaction.transaction_type === 'sell') {
            transactionQuantity -= transaction.quantity;
            // Calculate realized gain: (sell price - avg cost) * quantity sold
            const sellProceeds = transaction.quantity * transaction.price_per_share;
            const sellCost = transaction.quantity * avgCostPerShare;
            calculatedRealizedGain += (sellProceeds - sellCost);
            totalSellQuantity += transaction.quantity;
            totalSellProceeds += sellProceeds;
            // Adjust cost basis by removing the cost of sold shares
            transactionCost -= sellCost;
          }
        });
      }

      // Add stock dividends to quantity (they don't affect cost basis)
      let stockDividendQuantity = 0;
      let cashDividends = 0;
      if (dividendResponse.success && dividendResponse.dividends) {
        setDividends(dividendResponse.dividends);

        // Calculate total cash dividends
        cashDividends = dividendResponse.dividends
          .filter(d => d.dividend_type === 'cash')
          .reduce((sum, d) => sum + (Number(d.total_cash_received) || 0), 0);
        setTotalDividends(cashDividends);

        // Calculate stock dividend quantity
        stockDividendQuantity = dividendResponse.dividends
          .filter(d => d.dividend_type === 'stock')
          .reduce((sum, d) => sum + (Number(d.shares_received) || 0), 0);
      }

      // Total quantity includes stock dividends
      const totalQuantity = transactionQuantity + stockDividendQuantity;
      setCurrentQuantity(totalQuantity);
      setTotalCost(transactionCost);
      // Average cost is based on total quantity (including stock dividends)
      // Stock dividends increase quantity but don't change cost basis
      setAverageCost(totalQuantity > 0 ? transactionCost / totalQuantity : 0);

      // Calculate current value and gain/loss after both price and transactions are loaded
      if (priceResponse.success && priceResponse.prices && priceResponse.prices.length > 0) {
        const sortedPrices = [...priceResponse.prices].sort((a, b) =>
          new Date(b.price_date).getTime() - new Date(a.price_date).getTime()
        );
        const latestPrice = sortedPrices[0].price;

        const value = totalQuantity * latestPrice;

        // Unrealized gain is the gain on currently held shares
        const unrealizedGainValue = value - transactionCost;
        const gainPercent = transactionCost > 0 ? (unrealizedGainValue / transactionCost) * 100 : 0;

        // Total capital gain includes both realized and unrealized
        const totalCapitalGain = calculatedRealizedGain + unrealizedGainValue;

        setCurrentValue(value);
        setGainLoss(totalCapitalGain);
        setGainLossPercent(gainPercent);
        setRealizedGain(calculatedRealizedGain);
        setUnrealizedGain(unrealizedGainValue);

        // Calculate total return including cash dividends
        // Total return = realized gain + unrealized gain + cash dividends
        const totalRet = calculatedRealizedGain + unrealizedGainValue + cashDividends;
        // Calculate percentage based on original total investment (before any sells)
        const totalRetPercent = totalBuyCost > 0 ? (totalRet / totalBuyCost) * 100 : 0;

        setTotalReturn(totalRet);
        setTotalReturnPercent(totalRetPercent);
      }
    } catch (err) {
      console.error('Error loading company data:', err);
      setError('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = async () => {
    if (!stockCode || newPrice.price <= 0) return;

    try {
      const validation = stockPortfolioService.validatePriceData(newPrice);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await stockPortfolioService.updateStockPrice(newPrice);

      if (response.success) {
        setNewPrice({
          stock_code: stockCode,
          price: 0,
          price_date: stockPortfolioService.getCurrentDate()
        });
        setShowPriceForm(false);
        await loadCompanyData();
      } else {
        setError(response.message || 'Failed to update price');
      }
    } catch (err) {
      console.error('Error updating price:', err);
      setError('Failed to update price');
    }
  };

  const getTransactionsForDate = (date: string) => {
    return transactions.filter(t => t.transaction_date === date);
  };

  const getDividendsForDate = (date: string) => {
    return dividends.filter(d => d.payment_date === date);
  };

  const hasActivityOnDate = (date: string) => {
    return getTransactionsForDate(date).length > 0 || getDividendsForDate(date).length > 0;
  };

  const getActivityTypeForDate = (date: string): 'buy' | 'sell' | 'both' | 'dividend' | null => {
    const txns = getTransactionsForDate(date);
    const divs = getDividendsForDate(date);

    if (txns.length === 0 && divs.length === 0) return null;
    if (txns.length === 0 && divs.length > 0) return 'dividend';

    const hasBuy = txns.some(t => t.transaction_type === 'buy');
    const hasSell = txns.some(t => t.transaction_type === 'sell');

    if (hasBuy && hasSell) return 'both';
    if (hasBuy) return 'buy';
    if (hasSell) return 'sell';

    return null;
  };

  const handleStockSelect = (selectedStockCode: string) => {
    setShowStockDropdown(false);
    setStockSearchQuery('');
    navigate(`/finance/stocks/company/${selectedStockCode}`);
  };

  const handleStockInputFocus = () => {
    setShowStockDropdown(true);
    setStockSearchQuery('');
    if (allCompanies.length > 0) {
      setFilteredCompanies(allCompanies.filter(c => c.stock_code !== stockCode));
    }
  };

  const handleStockInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStockSearchQuery(e.target.value);
  };

  const handleStockInputBlur = () => {
    // Don't close dropdown immediately - let click handler manage it
    // This prevents dropdown from closing when clicking on an option
  };

  const handleCreateFinancialReport = async () => {
    if (!stockCode) return;

    try {
      const validation = stockPortfolioService.validateFinancialReportData(newFinancialReport);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await stockPortfolioService.createFinancialReport(newFinancialReport);

      if (response.success) {
        setNewFinancialReport({
          stock_code: stockCode,
          report_type: 'quarterly',
          fiscal_year: new Date().getFullYear(),
          quarter: 1,
          period_start_date: '',
          period_end_date: '',
          report_date: stockPortfolioService.getCurrentDate()
        });
        setShowFinancialReportForm(false);
        await loadFinancialReports();
      } else {
        setError(response.message || 'Failed to create financial report');
      }
    } catch (err) {
      console.error('Error creating financial report:', err);
      setError('Failed to create financial report');
    }
  };

  const handleUpdateFinancialReport = async () => {
    if (!editingReport) return;

    try {
      const updateData: Partial<FinancialReportFormData> = {
        ...newFinancialReport
      };
      delete (updateData as any).stock_code; // Don't update stock_code

      const response = await stockPortfolioService.updateFinancialReport(editingReport.id.toString(), updateData);

      if (response.success) {
        setEditingReport(null);
        setShowFinancialReportForm(false);
        setNewFinancialReport({
          stock_code: stockCode || '',
          report_type: 'quarterly',
          fiscal_year: new Date().getFullYear(),
          quarter: 1,
          period_start_date: '',
          period_end_date: '',
          report_date: stockPortfolioService.getCurrentDate()
        });
        await loadFinancialReports();
      } else {
        setError(response.message || 'Failed to update financial report');
      }
    } catch (err) {
      console.error('Error updating financial report:', err);
      setError('Failed to update financial report');
    }
  };

  const handleDeleteFinancialReport = async (reportId: number) => {
    if (!window.confirm('Are you sure you want to delete this financial report?')) {
      return;
    }

    try {
      const response = await stockPortfolioService.deleteFinancialReport(reportId.toString());

      if (response.success) {
        await loadFinancialReports();
      } else {
        setError(response.message || 'Failed to delete financial report');
      }
    } catch (err) {
      console.error('Error deleting financial report:', err);
      setError('Failed to delete financial report');
    }
  };

  const handleEditFinancialReport = (report: StockFinancialReport) => {
    setEditingReport(report);
    setNewFinancialReport({
      stock_code: report.stock_code,
      report_type: report.report_type,
      fiscal_year: report.fiscal_year,
      quarter: report.quarter,
      period_start_date: report.period_start_date,
      period_end_date: report.period_end_date,
      report_date: report.report_date,
      source: report.source,
      revenue: report.revenue,
      net_income: report.net_income,
      earnings_per_share: report.earnings_per_share,
      total_assets: report.total_assets,
      total_liabilities: report.total_liabilities,
      equity: report.equity,
      operating_cash_flow: report.operating_cash_flow,
      debt: report.debt,
      current_assets: report.current_assets,
      current_liabilities: report.current_liabilities,
      current_ratio: report.current_ratio,
      roe: report.roe,
      roa: report.roa,
      gross_profit_margin: report.gross_profit_margin,
      net_profit_margin: report.net_profit_margin,
      operating_profit_margin: report.operating_profit_margin,
      revenue_growth_rate: report.revenue_growth_rate,
      net_income_growth_rate: report.net_income_growth_rate,
      notes: report.notes
    });
    setShowFinancialReportForm(true);
  };

  const handleCancelFinancialReportForm = () => {
    setShowFinancialReportForm(false);
    setEditingReport(null);
    setNewFinancialReport({
      stock_code: stockCode || '',
      report_type: 'quarterly',
      fiscal_year: new Date().getFullYear(),
      quarter: 1,
      period_start_date: '',
      period_end_date: '',
      report_date: stockPortfolioService.getCurrentDate()
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sp-stock-selector')) {
        setShowStockDropdown(false);
        setStockSearchQuery('');
      }
      if (!target.closest('.sp-formula-popup-container')) {
        setOpenFormulaPopup(null);
      }
    };

    if (showStockDropdown || openFormulaPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStockDropdown, openFormulaPopup]);

  const renderFormulaPopup = (formulaType: string) => {
    const formulas: Record<string, { title: string; formula: string; explanation: string }> = {
      realizedGain: {
        title: 'Realized Gain',
        formula: 'Realized Gain = Σ[(Sell Price - Average Cost) × Quantity Sold]',
        explanation: 'This is the actual profit or loss you\'ve locked in from selling shares. For each sell transaction, we calculate the difference between the sell price and your average cost per share (based on all buy transactions), then multiply by the quantity sold. All realized gains from your sell transactions are summed together.'
      },
      unrealizedGain: {
        title: 'Unrealized Gain',
        formula: 'Unrealized Gain = Current Value - Remaining Cost Basis',
        explanation: 'This is the "on paper" profit or loss for shares you currently hold. It\'s calculated by taking the current market value of your holdings (current quantity × latest price) and subtracting your remaining cost basis (total cost after accounting for sold shares). This gain changes as the stock price fluctuates and only becomes "realized" when you sell the shares.'
      },
      cashDividends: {
        title: 'Cash Dividends',
        formula: 'Cash Dividends = Σ(Total Cash Received)',
        explanation: 'This is the total amount of cash you\'ve received from dividend payments. It includes all cash dividends paid out by the company, regardless of when they were received. Cash dividends are added directly to your total return as they represent actual income from your investment.'
      },
      totalReturn: {
        title: 'Total Return',
        formula: 'Total Return = Realized Gain + Unrealized Gain + Cash Dividends',
        explanation: 'This represents your complete investment performance. It combines all sources of return: the profit from shares you\'ve sold (realized gain), the potential profit on shares you still hold (unrealized gain), and all cash dividends received. This gives you a comprehensive view of how your investment has performed overall.'
      }
    };

    const formulaData = formulas[formulaType];
    if (!formulaData) return null;

    return (
      <div className="sp-formula-popup">
        <div className="sp-formula-popup-header">
          <span className="sp-formula-popup-title">{formulaData.title}</span>
          <button
            className="sp-formula-popup-close"
            onClick={() => setOpenFormulaPopup(null)}
          >
            ✕
          </button>
        </div>
        <div className="sp-formula-popup-content">
          <div className="sp-formula-equation">
            <code>{formulaData.formula}</code>
          </div>
          <div className="sp-formula-explanation">
            {formulaData.explanation}
          </div>
        </div>
      </div>
    );
  };

  const renderPriceChart = () => {
    if (priceHistory.length === 0) return null;

    // Sort prices by date ascending for chart
    const sortedPrices = [...priceHistory].sort((a, b) =>
      new Date(a.price_date).getTime() - new Date(b.price_date).getTime()
    );

    const prices = sortedPrices.map(p => p.price);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const priceRange = maxPrice - minPrice;

    return (
      <div className="sp-chart-container">
        <div className="sp-chart">
          <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="sp-chart-svg">
            <polyline
              fill="none"
              stroke="#333333"
              strokeWidth="0.5"
              points={sortedPrices.map((price, index) => {
                const x = (index / (sortedPrices.length - 1)) * 100;
                const y = priceRange > 0
                  ? 35 - ((price.price - minPrice) / priceRange) * 30
                  : 20;
                return `${x},${y}`;
              }).join(' ')}
            />
          </svg>
        </div>
        <div className="sp-chart-labels">
          <span className="sp-chart-label">{stockPortfolioService.formatCurrency(minPrice)}</span>
          <span className="sp-chart-label">{stockPortfolioService.formatCurrency(maxPrice)}</span>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="company-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <div className="sp-stock-selector" style={{ flex: 1 }}>
            <input
              type="text"
              className="sp-stock-input"
              value="Company Details"
              readOnly
              disabled
            />
          </div>
        </div>
        <div className="sp-auth-required">
          <p>Please sign in to view company details</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
          <div className="sp-stock-selector" style={{ flex: 1 }}>
            <input
              type="text"
              className="sp-stock-input"
              value={stockCode || 'Company Details'}
              readOnly
              disabled
            />
          </div>
        </div>
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading company data...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="company-detail">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
          <div className="sp-stock-selector" style={{ flex: 1 }}>
            <input
              type="text"
              className="sp-stock-input"
              value={stockCode || 'Company Details'}
              readOnly
              disabled
            />
          </div>
        </div>
        <div className="sp-empty-state">
          <p>Company not found</p>
          <button onClick={() => navigate('/finance/stocks')} className="sp-back-button">
            Back to Portfolio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="company-detail">
      <div className="sp-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <button className="sp-back-btn" onClick={() => navigate('/finance/stocks')}>←</button>
        <div className="sp-stock-selector" style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            className="sp-stock-input"
            value={showStockDropdown ? stockSearchQuery : company.stock_code}
            onChange={handleStockInputChange}
            onFocus={handleStockInputFocus}
            onBlur={handleStockInputBlur}
            placeholder="Search stock code or name..."
            autoComplete="off"
          />
          {showStockDropdown && (
            <div className="sp-stock-dropdown">
              {filteredCompanies.length === 0 ? (
                <div className="sp-stock-dropdown-empty">
                  No stocks found
                </div>
              ) : (
                filteredCompanies.map((company) => (
                  <div
                    key={company.stock_code}
                    className="sp-stock-option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleStockSelect(company.stock_code);
                    }}
                  >
                    <span className="sp-stock-option-code">{company.stock_code}</span>
                    <span className="sp-stock-option-name">{company.company_name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <button
          className="sp-add-btn"
          onClick={() => setShowPriceForm(!showPriceForm)}
          title="Update price"
        >
          {showPriceForm ? '✕' : '💰'}
        </button>
      </div>

      {error && (
        <div className="sp-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="sp-main-content">
        <div className="sp-tabs">
          <button
            className={`sp-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <span className="sp-tab-icon">📋</span>
            <span className="sp-tab-label">Overview</span>
          </button>
          <button
            className={`sp-tab-btn ${activeTab === 'price' ? 'active' : ''}`}
            onClick={() => setActiveTab('price')}
          >
            <span className="sp-tab-icon">📊</span>
            <span className="sp-tab-label">Price</span>
          </button>
          <button
            className={`sp-tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <span className="sp-tab-icon">💰</span>
            <span className="sp-tab-label">Activity</span>
          </button>
          <button
            className={`sp-tab-btn ${activeTab === 'financials' ? 'active' : ''}`}
            onClick={() => setActiveTab('financials')}
          >
            <span className="sp-tab-icon">📈</span>
            <span className="sp-tab-label">Financials</span>
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="sp-overview-section">
            <div className="sp-company-info">
              <div className="sp-info-section">
                <div className="sp-info-label">Stock Code</div>
                <div className="sp-info-value sp-code">{company.stock_code}</div>
              </div>
              <div className="sp-info-section">
                <div className="sp-info-label">Company Name</div>
                <div className="sp-info-value">{company.company_name}</div>
              </div>
              <div className="sp-info-section">
                <div className="sp-info-label">Strategy</div>
                <select
                  className="sp-info-value sp-strategy-select"
                  value={company.strategy || ''}
                  onChange={async (e) => {
                    const newStrategy = e.target.value as 'dividend' | 'growth' | '';
                    try {
                      const response = await stockPortfolioService.updateCompany(
                        company.stock_code,
                        { strategy: newStrategy || null }
                      );
                      if (response.success) {
                        // Reload company data to reflect the change
                        await loadCompanyData();
                      } else {
                        setError(response.message || 'Failed to update strategy');
                      }
                    } catch (err) {
                      console.error('Error updating strategy:', err);
                      setError('Failed to update strategy');
                    }
                  }}
                >
                  <option value="">Not Set</option>
                  <option value="dividend">Dividend</option>
                  <option value="growth">Growth</option>
                </select>
              </div>
            </div>

            {currentQuantity > 0 && (
              <div className="sp-holdings-summary">
                <div className="sp-summary-grid">
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Current Quantity</div>
                    <div className="sp-summary-value">{currentQuantity} shares</div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Current Price</div>
                    <div className="sp-summary-value">
                      {currentPrice > 0 ? stockPortfolioService.formatCurrency(currentPrice) : 'N/A'}
                    </div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Average Cost</div>
                    <div className="sp-summary-value">
                      {stockPortfolioService.formatCurrency(averageCost)}
                    </div>
                  </div>
                </div>
                <div className="sp-summary-grid">
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Total Cost</div>
                    <div className="sp-summary-value">
                      {stockPortfolioService.formatCurrency(totalCost)}
                    </div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Current Value</div>
                    <div className="sp-summary-value">
                      {currentPrice > 0 ? stockPortfolioService.formatCurrency(currentValue) : 'N/A'}
                    </div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">Capital Gain/Loss</div>
                    <div className="sp-summary-value">
                      {currentPrice > 0 ? stockPortfolioService.formatCurrency(gainLoss) : 'N/A'}
                    </div>
                  </div>
                </div>
                <div className="sp-summary-grid">
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">
                      <span>Realized Gain</span>
                      <div className="sp-formula-popup-container" style={{ position: 'relative', display: 'inline-block', marginLeft: '0.25rem' }}>
                        <button
                          className="sp-info-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFormulaPopup(openFormulaPopup === 'realizedGain' ? null : 'realizedGain');
                          }}
                          title="Show formula"
                        >
                          ⓘ
                        </button>
                        {openFormulaPopup === 'realizedGain' && renderFormulaPopup('realizedGain')}
                      </div>
                    </div>
                    <div className="sp-summary-value">
                      {stockPortfolioService.formatCurrency(realizedGain)}
                    </div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">
                      <span>Unrealized Gain</span>
                      <div className="sp-formula-popup-container" style={{ position: 'relative', display: 'inline-block', marginLeft: '0.25rem' }}>
                        <button
                          className="sp-info-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFormulaPopup(openFormulaPopup === 'unrealizedGain' ? null : 'unrealizedGain');
                          }}
                          title="Show formula"
                        >
                          ⓘ
                        </button>
                        {openFormulaPopup === 'unrealizedGain' && renderFormulaPopup('unrealizedGain')}
                      </div>
                    </div>
                    <div className="sp-summary-value">
                      {currentPrice > 0 ? stockPortfolioService.formatCurrency(unrealizedGain) : 'N/A'}
                    </div>
                  </div>
                  <div className="sp-summary-item">
                    <div className="sp-summary-label">
                      <span>Cash Dividends</span>
                      <div className="sp-formula-popup-container" style={{ position: 'relative', display: 'inline-block', marginLeft: '0.25rem' }}>
                        <button
                          className="sp-info-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFormulaPopup(openFormulaPopup === 'cashDividends' ? null : 'cashDividends');
                          }}
                          title="Show formula"
                        >
                          ⓘ
                        </button>
                        {openFormulaPopup === 'cashDividends' && renderFormulaPopup('cashDividends')}
                      </div>
                    </div>
                    <div className="sp-summary-value">
                      {stockPortfolioService.formatCurrency(totalDividends)}
                    </div>
                  </div>
                </div>
                <div className="sp-summary-grid">
                  <div className={`sp-summary-item sp-summary-highlight ${totalReturn >= 0 ? 'positive' : 'negative'}`}>
                    <div className="sp-summary-label">
                      <span>Total Return</span>
                      <div className="sp-formula-popup-container" style={{ position: 'relative', display: 'inline-block', marginLeft: '0.25rem' }}>
                        <button
                          className="sp-info-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFormulaPopup(openFormulaPopup === 'totalReturn' ? null : 'totalReturn');
                          }}
                          title="Show formula"
                        >
                          ⓘ
                        </button>
                        {openFormulaPopup === 'totalReturn' && renderFormulaPopup('totalReturn')}
                      </div>
                    </div>
                    <div className="sp-summary-value-large">
                      {currentPrice > 0 ? (
                        <>
                          <span className="sp-gain-amount">{stockPortfolioService.formatCurrency(totalReturn)}</span>
                          <span className="sp-gain-percent">{stockPortfolioService.formatPercentage(totalReturnPercent)}</span>
                        </>
                      ) : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'price' && (
          <>
            {showPriceForm && (
              <div className="sp-price-form">
                <h3>Update Stock Price</h3>
                <div className="sp-form-row">
                  <input
                    type="number"
                    placeholder="Price"
                    value={newPrice.price || ''}
                    onChange={(e) => setNewPrice({ ...newPrice, price: parseFloat(e.target.value) || 0 })}
                    className="sp-price-input"
                    min="0"
                    step="0.01"
                  />
                  <input
                    type="date"
                    value={newPrice.price_date}
                    onChange={(e) => setNewPrice({ ...newPrice, price_date: e.target.value })}
                    className="sp-date-input"
                  />
                </div>
                <div className="sp-form-actions">
                  <button
                    onClick={handleUpdatePrice}
                    className="sp-save-btn"
                    disabled={newPrice.price <= 0}
                  >
                    Update Price
                  </button>
                  <button onClick={() => {
                    setShowPriceForm(false);
                    setNewPrice({
                      stock_code: stockCode || '',
                      price: 0,
                      price_date: stockPortfolioService.getCurrentDate()
                    });
                  }} className="sp-cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="sp-price-history">
              {priceHistory.length === 0 ? (
                <div className="sp-empty-state">
                  <p>No price history available</p>
                  <button onClick={() => setShowPriceForm(true)} className="sp-add-first-btn">
                    Add first price
                  </button>
                </div>
              ) : (
                <>
                  {renderPriceChart()}
                  <div className="sp-price-list">
                    {[...priceHistory]
                      .sort((a, b) => new Date(b.price_date).getTime() - new Date(a.price_date).getTime())
                      .map((price, index, arr) => {
                        const hasActivity = hasActivityOnDate(price.price_date);
                        const activityType = getActivityTypeForDate(price.price_date);
                        const isExpanded = expandedPriceDate === price.price_date;
                        const dateTransactions = getTransactionsForDate(price.price_date);
                        const dateDividends = getDividendsForDate(price.price_date);

                        return (
                          <div key={`price-${price.id}`} className={`sp-price-item ${index === arr.length - 1 ? 'last' : ''} ${isExpanded ? 'expanded' : ''}`}>
                            <div className="sp-price-item-header">
                              <div className="sp-price-date">
                                {new Date(price.price_date).toLocaleDateString()}
                              </div>
                              <div className="sp-price-item-right">
                                <div className="sp-price-value">
                                  {stockPortfolioService.formatCurrency(price.price)}
                                </div>
                                {hasActivity && activityType && (
                                  <button
                                    className={`sp-price-info-icon sp-activity-${activityType}`}
                                    onClick={() => setExpandedPriceDate(isExpanded ? null : price.price_date)}
                                    title="View transactions"
                                  >
                                    ⓘ
                                  </button>
                                )}
                              </div>
                            </div>

                            {isExpanded && hasActivity && (
                              <div className="sp-price-activity-details">
                                {dateTransactions.map(transaction => {
                                  const isBuy = transaction.transaction_type === 'buy';
                                  const totalValue = transaction.quantity * transaction.price_per_share;

                                  return (
                                    <div
                                      key={`txn-${transaction.id}`}
                                      className={`sp-price-activity-item sp-transaction-${transaction.transaction_type}`}
                                      onClick={() => navigate(`/finance/stocks/transaction/${transaction.id}`)}
                                    >
                                      <div className="sp-activity-type">
                                        <span className="sp-activity-icon">{isBuy ? '🟢' : '🔴'}</span>
                                        <span className="sp-activity-label">{isBuy ? 'Buy' : 'Sell'}</span>
                                      </div>
                                      <div className="sp-activity-details-text">
                                        <span>{transaction.quantity} shares × {stockPortfolioService.formatCurrency(transaction.price_per_share)}</span>
                                        <span className="sp-activity-total">Total: {stockPortfolioService.formatCurrency(totalValue)}</span>
                                      </div>
                                    </div>
                                  );
                                })}

                                {dateDividends.map(dividend => {
                                  const isCash = dividend.dividend_type === 'cash';

                                  return (
                                    <div
                                      key={`div-${dividend.id}`}
                                      className="sp-price-activity-item sp-dividend-item"
                                      onClick={() => navigate(`/finance/stocks/dividend/${dividend.id}`)}
                                    >
                                      <div className="sp-activity-type">
                                        <span className="sp-activity-icon">{isCash ? '💵' : '📈'}</span>
                                        <span className="sp-activity-label">{isCash ? 'Cash Dividend' : 'Stock Dividend'}</span>
                                      </div>
                                      <div className="sp-activity-details-text">
                                        {isCash ? (
                                          <span>{stockPortfolioService.formatCurrency(dividend.total_cash_received || 0)}</span>
                                        ) : (
                                          <span>{dividend.shares_received} shares received</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'transactions' && (
          <div className="sp-transactions-section">
            {transactions.length === 0 && dividends.length === 0 ? (
              <div className="sp-empty-state">
                <p>No activity available</p>
              </div>
            ) : (
              <div className="sp-transactions-list">
                {[
                  ...transactions.map(t => ({ type: 'transaction' as const, data: t, date: t.transaction_date, id: `txn-${t.id}` })),
                  ...dividends.map(d => ({ type: 'dividend' as const, data: d, date: d.payment_date, id: `div-${d.id}` }))
                ]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((activity, index, arr) => {
                    if (activity.type === 'transaction') {
                      const transaction = activity.data as StockTransaction;
                      const isBuy = transaction.transaction_type === 'buy';
                      const totalValue = transaction.quantity * transaction.price_per_share;
                      const isLast = index === arr.length - 1;

                      return (
                        <div
                          key={activity.id}
                          className={`sp-activity-item sp-transaction-item sp-transaction-${transaction.transaction_type} ${isLast ? 'last' : ''}`}
                          onClick={() => navigate(`/finance/stocks/transaction/${transaction.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="sp-transaction-header">
                            <div className="sp-transaction-type-icon">
                              {isBuy ? '🟢' : '🔴'}
                            </div>
                            <div className="sp-transaction-content">
                              <div className="sp-transaction-main">
                                <span className="sp-transaction-type-badge">{isBuy ? 'Buy' : 'Sell'}</span>
                                <span className="sp-transaction-date">
                                  {new Date(transaction.transaction_date).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="sp-transaction-info">
                                <span className="sp-transaction-quantity">{transaction.quantity} shares</span>
                                <span className="sp-transaction-separator">×</span>
                                <span className="sp-transaction-price">
                                  {stockPortfolioService.formatCurrency(transaction.price_per_share)}
                                </span>
                              </div>
                              <div className="sp-transaction-total">
                                Total: {stockPortfolioService.formatCurrency(totalValue)}
                              </div>
                              {transaction.notes && (
                                <div className="sp-transaction-notes">{transaction.notes}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      const dividend = activity.data as StockDividend;
                      const isCash = dividend.dividend_type === 'cash';
                      const isLast = index === arr.length - 1;

                      return (
                        <div
                          key={activity.id}
                          className={`sp-activity-item sp-dividend-item ${isLast ? 'last' : ''}`}
                          onClick={() => navigate(`/finance/stocks/dividend/${dividend.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="sp-transaction-header">
                            <div className="sp-transaction-type-icon">
                              {isCash ? '💵' : '📈'}
                            </div>
                            <div className="sp-transaction-content">
                              <div className="sp-transaction-main">
                                <span className={`sp-transaction-type-badge dividend-badge ${dividend.dividend_type}`}>
                                  {isCash ? 'Cash Dividend' : 'Stock Dividend'}
                                </span>
                                <span className="sp-transaction-date">
                                  {new Date(dividend.payment_date).toLocaleDateString()}
                                </span>
                              </div>
                              {isCash ? (
                                <div className="sp-transaction-total">
                                  Amount: {stockPortfolioService.formatCurrency(dividend.total_cash_received || 0)}
                                </div>
                              ) : (
                                <div className="sp-transaction-info">
                                  <span className="sp-transaction-quantity">{dividend.shares_received} shares received</span>
                                  {dividend.stock_ratio && (
                                    <span className="sp-transaction-price">Ratio: {dividend.stock_ratio}</span>
                                  )}
                                </div>
                              )}
                              {dividend.notes && (
                                <div className="sp-transaction-notes">{dividend.notes}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'financials' && (
          <div className="sp-financials-section">
            <div className="sp-financials-header">
              <div className="sp-financials-filters">
                <button
                  className={`sp-filter-btn ${reportFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setReportFilter('all')}
                >
                  All
                </button>
                <button
                  className={`sp-filter-btn ${reportFilter === 'quarterly' ? 'active' : ''}`}
                  onClick={() => setReportFilter('quarterly')}
                >
                  Quarterly
                </button>
                <button
                  className={`sp-filter-btn ${reportFilter === 'yearly' ? 'active' : ''}`}
                  onClick={() => setReportFilter('yearly')}
                >
                  Yearly
                </button>
              </div>
              <button
                className="sp-add-btn"
                onClick={() => {
                  setShowFinancialReportForm(true);
                  setEditingReport(null);
                }}
                title="Add financial report"
              >
                {showFinancialReportForm ? '✕' : '+'}
              </button>
            </div>

            {showFinancialReportForm && (
              <div className="sp-financial-report-form">
                <h3>{editingReport ? 'Edit Financial Report' : 'Add Financial Report'}</h3>
                <div className="sp-form-grid">
                  <div className="sp-form-group">
                    <label>Report Type</label>
                    <select
                      value={newFinancialReport.report_type}
                      onChange={(e) => {
                        const reportType = e.target.value as 'quarterly' | 'yearly';
                        setNewFinancialReport({
                          ...newFinancialReport,
                          report_type: reportType,
                          quarter: reportType === 'quarterly' ? 1 : null
                        });
                      }}
                      className="sp-form-input"
                    >
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="sp-form-group">
                    <label>Fiscal Year</label>
                    <input
                      type="number"
                      value={newFinancialReport.fiscal_year}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, fiscal_year: parseInt(e.target.value) || 0 })}
                      className="sp-form-input"
                      min="1900"
                      max="2100"
                    />
                  </div>
                  {newFinancialReport.report_type === 'quarterly' && (
                    <div className="sp-form-group">
                      <label>Quarter</label>
                      <select
                        value={newFinancialReport.quarter || 1}
                        onChange={(e) => setNewFinancialReport({ ...newFinancialReport, quarter: parseInt(e.target.value) })}
                        className="sp-form-input"
                      >
                        <option value="1">Q1</option>
                        <option value="2">Q2</option>
                        <option value="3">Q3</option>
                        <option value="4">Q4</option>
                      </select>
                    </div>
                  )}
                  <div className="sp-form-group">
                    <label>Period Start Date</label>
                    <input
                      type="date"
                      value={newFinancialReport.period_start_date}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, period_start_date: e.target.value })}
                      className="sp-form-input"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Period End Date</label>
                    <input
                      type="date"
                      value={newFinancialReport.period_end_date}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, period_end_date: e.target.value })}
                      className="sp-form-input"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Report Date</label>
                    <input
                      type="date"
                      value={newFinancialReport.report_date}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, report_date: e.target.value })}
                      className="sp-form-input"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Source</label>
                    <input
                      type="text"
                      value={newFinancialReport.source || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, source: e.target.value })}
                      className="sp-form-input"
                      placeholder="e.g., Company Website"
                    />
                  </div>
                </div>
                <div className="sp-form-section-title">Financial Metrics</div>
                <div className="sp-form-grid">
                  <div className="sp-form-group">
                    <label>Revenue</label>
                    <input
                      type="number"
                      value={newFinancialReport.revenue || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, revenue: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Net Income</label>
                    <input
                      type="number"
                      value={newFinancialReport.net_income || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, net_income: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Earnings Per Share</label>
                    <input
                      type="number"
                      value={newFinancialReport.earnings_per_share || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, earnings_per_share: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.0001"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Total Assets</label>
                    <input
                      type="number"
                      value={newFinancialReport.total_assets || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, total_assets: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Total Liabilities</label>
                    <input
                      type="number"
                      value={newFinancialReport.total_liabilities || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, total_liabilities: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Equity</label>
                    <input
                      type="number"
                      value={newFinancialReport.equity || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, equity: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Operating Cash Flow</label>
                    <input
                      type="number"
                      value={newFinancialReport.operating_cash_flow || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, operating_cash_flow: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Debt</label>
                    <input
                      type="number"
                      value={newFinancialReport.debt || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, debt: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Current Assets</label>
                    <input
                      type="number"
                      value={newFinancialReport.current_assets || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, current_assets: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Current Liabilities</label>
                    <input
                      type="number"
                      value={newFinancialReport.current_liabilities || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, current_liabilities: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Current Ratio</label>
                    <input
                      type="number"
                      value={newFinancialReport.current_ratio || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, current_ratio: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.0001"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>ROE (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.roe || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, roe: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>ROA (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.roa || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, roa: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Gross Profit Margin (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.gross_profit_margin || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, gross_profit_margin: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Net Profit Margin (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.net_profit_margin || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, net_profit_margin: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Operating Profit Margin (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.operating_profit_margin || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, operating_profit_margin: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Revenue Growth Rate (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.revenue_growth_rate || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, revenue_growth_rate: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                  <div className="sp-form-group">
                    <label>Net Income Growth Rate (%)</label>
                    <input
                      type="number"
                      value={newFinancialReport.net_income_growth_rate || ''}
                      onChange={(e) => setNewFinancialReport({ ...newFinancialReport, net_income_growth_rate: parseFloat(e.target.value) || undefined })}
                      className="sp-form-input"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="sp-form-group">
                  <label>Notes</label>
                  <textarea
                    value={newFinancialReport.notes || ''}
                    onChange={(e) => setNewFinancialReport({ ...newFinancialReport, notes: e.target.value })}
                    className="sp-form-input"
                    rows={3}
                    placeholder="Additional notes about this report..."
                  />
                </div>
                <div className="sp-form-actions">
                  <button
                    onClick={editingReport ? handleUpdateFinancialReport : handleCreateFinancialReport}
                    className="sp-save-btn"
                  >
                    {editingReport ? 'Update Report' : 'Create Report'}
                  </button>
                  <button onClick={handleCancelFinancialReportForm} className="sp-cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="sp-financial-reports-list">
              {financialReports.length === 0 ? (
                <div className="sp-empty-state">
                  <p>No financial reports available</p>
                  <button onClick={() => setShowFinancialReportForm(true)} className="sp-add-first-btn">
                    Add first report
                  </button>
                </div>
              ) : (
                <div className="sp-reports-grouped">
                  {Object.entries(
                    financialReports.reduce((acc, report) => {
                      const year = report.fiscal_year;
                      if (!acc[year]) acc[year] = [];
                      acc[year].push(report);
                      return acc;
                    }, {} as Record<number, StockFinancialReport[]>)
                  )
                    .sort(([a], [b]) => parseInt(b) - parseInt(a))
                    .map(([year, reports]) => (
                      <div key={year} className="sp-reports-year-group">
                        <div className="sp-reports-year-header">{year}</div>
                        {reports.map((report, index) => {
                          const isExpanded = expandedReportId === report.id;
                          return (
                            <div
                              key={report.id}
                              className={`sp-financial-report-item ${index === reports.length - 1 ? 'last' : ''} ${isExpanded ? 'expanded' : ''}`}
                            >
                              <div
                                className="sp-financial-report-header"
                                onClick={() => setExpandedReportId(isExpanded ? null : report.id)}
                              >
                                <div className="sp-financial-report-title">
                                  <span className="sp-report-type-badge">{report.report_type === 'quarterly' ? `Q${report.quarter}` : 'Yearly'}</span>
                                  <span className="sp-report-period">
                                    {new Date(report.period_start_date).toLocaleDateString()} - {new Date(report.period_end_date).toLocaleDateString()}
                                  </span>
                                </div>
                                <div className="sp-financial-report-actions">
                                  {report.revenue && (
                                    <span className="sp-report-value">{stockPortfolioService.formatCurrency(report.revenue)}</span>
                                  )}
                                  <span className="sp-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="sp-financial-report-details">
                                  <div className="sp-financial-metrics-grid">
                                    {report.revenue !== null && report.revenue !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Revenue</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.revenue)}</span>
                                      </div>
                                    )}
                                    {report.net_income !== null && report.net_income !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Net Income</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.net_income)}</span>
                                      </div>
                                    )}
                                    {report.earnings_per_share !== null && report.earnings_per_share !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">EPS</span>
                                        <span className="sp-metric-value">{Number(report.earnings_per_share).toFixed(4)}</span>
                                      </div>
                                    )}
                                    {report.total_assets !== null && report.total_assets !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Total Assets</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.total_assets)}</span>
                                      </div>
                                    )}
                                    {report.total_liabilities !== null && report.total_liabilities !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Total Liabilities</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.total_liabilities)}</span>
                                      </div>
                                    )}
                                    {report.equity !== null && report.equity !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Equity</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.equity)}</span>
                                      </div>
                                    )}
                                    {report.operating_cash_flow !== null && report.operating_cash_flow !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Operating Cash Flow</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.operating_cash_flow)}</span>
                                      </div>
                                    )}
                                    {report.debt !== null && report.debt !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Debt</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.debt)}</span>
                                      </div>
                                    )}
                                    {report.current_assets !== null && report.current_assets !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Current Assets</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.current_assets)}</span>
                                      </div>
                                    )}
                                    {report.current_liabilities !== null && report.current_liabilities !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Current Liabilities</span>
                                        <span className="sp-metric-value">{stockPortfolioService.formatCurrency(report.current_liabilities)}</span>
                                      </div>
                                    )}
                                    {report.current_ratio !== null && report.current_ratio !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Current Ratio</span>
                                        <span className="sp-metric-value">{Number(report.current_ratio).toFixed(4)}</span>
                                      </div>
                                    )}
                                    {report.roe !== null && report.roe !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">ROE</span>
                                        <span className="sp-metric-value">{Number(report.roe).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.roa !== null && report.roa !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">ROA</span>
                                        <span className="sp-metric-value">{Number(report.roa).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.gross_profit_margin !== null && report.gross_profit_margin !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Gross Profit Margin</span>
                                        <span className="sp-metric-value">{Number(report.gross_profit_margin).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.net_profit_margin !== null && report.net_profit_margin !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Net Profit Margin</span>
                                        <span className="sp-metric-value">{Number(report.net_profit_margin).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.operating_profit_margin !== null && report.operating_profit_margin !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Operating Profit Margin</span>
                                        <span className="sp-metric-value">{Number(report.operating_profit_margin).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.revenue_growth_rate !== null && report.revenue_growth_rate !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Revenue Growth Rate</span>
                                        <span className="sp-metric-value">{Number(report.revenue_growth_rate).toFixed(2)}%</span>
                                      </div>
                                    )}
                                    {report.net_income_growth_rate !== null && report.net_income_growth_rate !== undefined && (
                                      <div className="sp-metric-item">
                                        <span className="sp-metric-label">Net Income Growth Rate</span>
                                        <span className="sp-metric-value">{Number(report.net_income_growth_rate).toFixed(2)}%</span>
                                      </div>
                                    )}
                                  </div>
                                  {report.source && (
                                    <div className="sp-report-source">
                                      <span className="sp-report-source-label">Source:</span>
                                      <span className="sp-report-source-value">{report.source}</span>
                                    </div>
                                  )}
                                  {report.notes && (
                                    <div className="sp-report-notes">
                                      <span className="sp-report-notes-label">Notes:</span>
                                      <span className="sp-report-notes-value">{report.notes}</span>
                                    </div>
                                  )}
                                  <div className="sp-report-actions">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditFinancialReport(report);
                                      }}
                                      className="sp-edit-btn"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFinancialReport(report.id);
                                      }}
                                      className="sp-delete-btn"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetail;

